"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import {
  ArrowRight,
  Award,
  CheckCircle,
  DollarSign,
  Globe,
  Mail,
  TrendingUp,
  Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import investorsHeroBg from "@/public/images/market.jpeg";

// Hero Section
const InvestorHero = () => {
  const prefersReducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const parallaxY = useTransform(
    scrollY,
    [0, 400],
    [0, prefersReducedMotion ? 0 : 32],
  );

  return (
    <section
      aria-labelledby="investors-hero-title"
      id="investors-hero"
      className="relative w-full px-4 py-24 mx-auto max-w-screen-xl text-center overflow-hidden min-h-[70vh] flex items-center justify-center"
    >
      {/* Background Image */}
      <motion.div
        className="absolute inset-0 z-0"
        aria-hidden="true"
        style={{ y: parallaxY, willChange: "transform" }}
      >
        <Image
          src={investorsHeroBg}
          alt="Global financial markets skyline"
          fill
          priority
          placeholder="blur"
          quality={80}
          decoding="async"
          sizes="100vw"
          className="object-cover object-center md:object-[center_35%] w-full h-full dark:opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/60 to-background/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/20 via-transparent to-background/20" />
      </motion.div>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_70%)] pointer-events-none" />
      <div className="absolute inset-0 bg-geometric pointer-events-none" />
      <div className="absolute inset-0 floating-orbs pointer-events-none" />

      {/* Floating Icons */}
      <div className="absolute top-20 left-10 w-16 h-16 bg-primary/5 rounded-lg hidden md:flex items-center justify-center animate-float motion-reduce:animate-none">
        <DollarSign className="w-8 h-8 text-primary/30" />
      </div>
      <div
        className="absolute top-32 right-16 w-12 h-12 bg-green-500/5 rounded-full hidden md:flex items-center justify-center animate-float motion-reduce:animate-none"
        style={{ animationDelay: "2s" }}
      >
        <TrendingUp className="w-6 h-6 text-green-500/30" />
      </div>
      <div
        className="absolute bottom-32 left-20 w-14 h-14 bg-blue-500/5 rounded-lg hidden md:flex items-center justify-center animate-float motion-reduce:animate-none"
        style={{ animationDelay: "4s" }}
      >
        <Globe className="w-7 h-7 text-blue-500/30" />
      </div>

      <div className="relative z-20">
        <h1
          id="investors-hero-title"
          className="text-4xl font-bold leading-tight tracking-tighter md:text-5xl lg:text-6xl animate-gradient bg-gradient-to-r from-primary via-green-600 to-blue-600 bg-clip-text text-transparent"
        >
          Investment Opportunity
        </h1>
        <p className="text-xl md:text-2xl font-semibold mt-4 text-foreground animate-pulse-glow">
          Join the Future of AI-Powered Analytics
        </p>
        <p className="mt-6 text-lg text-muted-foreground md:text-xl max-w-4xl mx-auto">
          We're building the future of AI-powered analytics and financial market
          intelligence. Join us in revolutionizing how businesses leverage data
          and AI to drive unprecedented growth.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
          <Button size="lg" className="text-lg px-8 py-4 shimmer card-hover">
            <Mail className="w-5 h-5 mr-2" />
            Contact Founder
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="text-lg px-8 py-4 card-hover"
          >
            Request Investment Deck
          </Button>
        </div>
      </div>
    </section>
  );
};

// Investment Opportunity Section
const InvestmentOpportunity = () => (
  <section className="relative py-24 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-950/20 dark:to-gray-950/20">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Seeking Strategic Partners & Investors
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
          Kautious is at the forefront of AI-driven analytics and financial
          market intelligence. We're transforming how enterprises handle data,
          make decisions, and drive growth through Kautious and the governed
          Llamox platform behind it.
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <Card className="p-8 card-hover bg-background/95 dark:bg-background/80 backdrop-blur-sm border-primary/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full" />
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-blue-500/20 rounded-lg flex items-center justify-center animate-pulse-glow">
                <DollarSign className="w-8 h-8 text-primary" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-semibold mb-4 gradient-text">
                Why Invest in Kautious?
              </h3>
              <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
                We're positioned at the intersection of two massive growth
                markets: AI analytics and financial technology. Our proprietary
                technology and deep domain expertise create a unique competitive
                advantage.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="font-semibold mb-2">Market Opportunity</h4>
                  <p className="text-sm text-muted-foreground">
                    Multi-billion dollar addressable market in AI analytics and
                    financial technology
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-semibold mb-2">Competitive Advantage</h4>
                  <p className="text-sm text-muted-foreground">
                    Proprietary serverless AI platform with proven financial
                    markets expertise
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Globe className="w-6 h-6 text-purple-600" />
                  </div>
                  <h4 className="font-semibold mb-2">Scalable Technology</h4>
                  <p className="text-sm text-muted-foreground">
                    Cloud-native architecture designed for rapid global
                    expansion
                  </p>
                </div>
              </div>

              <div className="bg-primary/5 p-6 rounded-lg mb-6">
                <p className="text-sm text-muted-foreground font-medium">
                  <strong className="text-primary">Important:</strong> We
                  welcome discussions with serious investors only. This includes
                  institutional investors, family offices, and high-net-worth
                  individuals with a genuine interest in long-term strategic
                  partnership and substantial investment capacity.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </section>
);

// Investment Highlights Section
const InvestmentHighlights = () => (
  <section className="relative py-24">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Investment Highlights
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Key factors that make Kautious an attractive investment opportunity
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card className="p-6 card-hover">
          <CardHeader>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-xl">Proven Market Traction</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base">
              Already serving enterprise customers with our AI-powered analytics
              platform. Demonstrated product-market fit with growing revenue and
              customer base.
            </CardDescription>
          </CardContent>
        </Card>

        <Card className="p-6 card-hover">
          <CardHeader>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle className="text-xl">Proprietary Technology</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base">
              Our Llamox platform represents years of R&D investment in
              governed, serverless AI architecture. Unique competitive moat with
              patent-pending innovations.
            </CardDescription>
          </CardContent>
        </Card>

        <Card className="p-6 card-hover">
          <CardHeader>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mb-4">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
            <CardTitle className="text-xl">Global Expansion Ready</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base">
              Cloud-native architecture enables rapid scaling across markets.
              Strong partnerships and distribution channels already established.
            </CardDescription>
          </CardContent>
        </Card>

        <Card className="p-6 card-hover">
          <CardHeader>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <CardTitle className="text-xl">Experienced Team</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base">
              Leadership team with proven track record in AI, fintech, and
              enterprise software. Deep domain expertise in financial markets
              and risk management.
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// Microsoft for Startups Section
const MicrosoftStartupsSection = () => (
  <section className="relative py-16 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
    <div className="max-w-screen-xl mx-auto px-6">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold mb-3">
          Backed by Industry Leaders
        </h2>
        <p className="text-lg text-muted-foreground">
          Recognized and supported by the world's leading technology companies
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {/* Microsoft for Startups */}
        <Card className="p-8 card-hover bg-background/95 dark:bg-background/80 backdrop-blur-sm border-blue-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-bl-full" />
          <div className="relative z-10 text-center">
            <Link
              href="https://www.microsoft.com/en-us/startups"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="w-48 h-48 bg-white dark:bg-slate-800 rounded-lg flex items-center justify-center p-6 shadow-lg mx-auto mb-4 transition-transform hover:scale-105">
                <Image
                  src="/images/microsoft.jpg"
                  alt="Microsoft for Startups"
                  width={160}
                  height={160}
                  className="object-contain"
                />
              </div>
            </Link>
            <h3 className="text-lg font-bold gradient-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
              Microsoft for Startups
            </h3>
            <p className="text-sm text-muted-foreground">
              $150,000+ in Azure Credits
            </p>
          </div>
        </Card>

        {/* Daytona Startup Grid */}
        <Card className="p-8 card-hover bg-background/95 dark:bg-background/80 backdrop-blur-sm border-emerald-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-emerald-500/10 to-transparent rounded-bl-full" />
          <div className="relative z-10 text-center">
            <Link
              href="https://daytona.io/startups?utm_source=kautious.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="w-48 h-48 bg-white dark:bg-slate-800 rounded-lg flex items-center justify-center p-6 shadow-lg mx-auto mb-4 transition-transform hover:scale-105">
                <img
                  src="https://img.shields.io/badge/SPONSORED%20BY-DAYTONA%20STARTUP%20GRID-2ECC71?style=for-the-badge"
                  alt="Daytona Startup Grid"
                  className="object-contain max-w-full max-h-full"
                />
              </div>
            </Link>
            <h3 className="text-lg font-bold gradient-text bg-gradient-to-r from-emerald-600 to-green-600 mb-2">
              Daytona Startup Grid
            </h3>
            <p className="text-sm text-muted-foreground">
              Startup Credits & Support
            </p>
          </div>
        </Card>

        {/* E2B.dev */}
        <Card className="p-8 card-hover bg-background/95 dark:bg-background/80 backdrop-blur-sm border-purple-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-500/10 to-transparent rounded-bl-full" />
          <div className="relative z-10 text-center">
            <Link
              href="https://e2b.dev/startups"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="w-48 h-48 bg-white dark:bg-slate-800 rounded-lg flex items-center justify-center p-6 shadow-lg mx-auto mb-4 transition-transform hover:scale-105">
                <Image
                  src="/images/e2b.png"
                  alt="E2B.dev"
                  width={160}
                  height={160}
                  className="object-contain"
                />
              </div>
            </Link>
            <h3 className="text-lg font-bold gradient-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
              E2B.dev Partner
            </h3>
            <p className="text-sm text-muted-foreground">
              Secure AI Code Execution
            </p>
          </div>
        </Card>
      </div>
    </div>
  </section>
);

// Contact Section
const ContactSection = () => (
  <section className="relative py-24 bg-primary text-primary-foreground">
    <div className="max-w-screen-xl mx-auto px-6 text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-6 !text-white dark:!text-slate-900">
        Ready to Explore the Opportunity?
      </h2>
      <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
        If you're a serious investor interested in partnering with us to build
        the future of AI-powered analytics, we'd love to hear from you. Let's
        discuss how we can create value together.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
          <Mail className="w-5 h-5 mr-2" />
          Contact Founder
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-lg px-8 py-4 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
        >
          Request Investment Deck
        </Button>
      </div>
    </div>
  </section>
);

export default function InvestorsPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        <InvestorHero />
        <InvestmentOpportunity />
        <InvestmentHighlights />
        <MicrosoftStartupsSection />
        <ContactSection />
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
